import { FC, useMemo } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { CallIntelQueries, CallIntelTypes } from '@frontend/api-call-intel';
import { CallIntelChip } from '@frontend/call-intel-insights';
import { useTranslation } from '@frontend/i18n';
import { Icon, IconColorType, IconName } from '@frontend/icons';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { FontColorType, Text, styles as commonStyles, useTooltip } from '@frontend/design-system';

dayjs.extend(utc);

const API_DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss.SSS';

export const SummaryPanel: FC<{ locationIds: string[] }> = ({ locationIds = [] }) => {
  const { t } = useTranslation('callIntelDashboardWidgets');

  const { currentWeekOverviewRequest, previousWeekOverviewRequest } = useMemo(() => {
    const commonFilters: CallIntelTypes.ApiPayloadProps['filters'] = {
      sentiments: [CallIntelTypes.SentimentEnum.SENTIMENT_NEGATIVE],
      locations: locationIds,
    };

    const currentWeekOverviewRequest: CallIntelTypes.ApiPayloadProps = {
      filters: {
        ...commonFilters,
        startDate: dayjs().startOf('week').utc().format(API_DATE_FORMAT),
        endDate: dayjs().endOf('week').utc().format(API_DATE_FORMAT),
      },
      includesAnalysis: [CallIntelTypes.IncludesAnalysis.CALL_SENTIMENT],
    };

    const previousWeekOverviewRequest: CallIntelTypes.ApiPayloadProps = {
      filters: {
        ...commonFilters,
        startDate: dayjs().subtract(1, 'week').startOf('week').utc().format(API_DATE_FORMAT),
        endDate: dayjs().subtract(1, 'week').endOf('week').utc().format(API_DATE_FORMAT),
      },
      includesAnalysis: [CallIntelTypes.IncludesAnalysis.CALL_SENTIMENT],
    };

    return {
      currentWeekOverviewRequest,
      previousWeekOverviewRequest,
    };
  }, [locationIds]);

  const { data: currentWeekOverview } = CallIntelQueries.useGetOverviewDetails<
    CallIntelTypes.GetOverviewStatsIO['output']
  >({
    isDemoAccount: false,
    demoData: null as any, // Adding any as this field will be ignored
    payload: currentWeekOverviewRequest,
    realQueryOptions: {
      staleTime: 5 * 60 * 1000,
    },
  });

  const { data: totalCallsAnalyzed = 0 } = CallIntelQueries.useGetOverviewStats({
    demoData: null,
    isDemoAccount: false,
    payload: {
      filters: currentWeekOverviewRequest.filters,
      includes: { opportunities: false, serviceQuality: false },
    },
    realQueryOptions: {
      staleTime: 5 * 60 * 1000,
      select: (response) => response.totalCallsAnalyzed || 0,
    },
  });

  const { data: previousWeekTotalCallsAnalyzed = 0 } = CallIntelQueries.useGetOverviewStats({
    demoData: null,
    isDemoAccount: false,
    payload: {
      filters: previousWeekOverviewRequest.filters,
      includes: { opportunities: false, serviceQuality: false },
    },
    realQueryOptions: {
      staleTime: 5 * 60 * 1000,
      select: (response) => response.totalCallsAnalyzed || 0,
    },
  });

  const { data: previousWeekOverview } = CallIntelQueries.useGetOverviewDetails<
    CallIntelTypes.GetOverviewStatsIO['output']
  >({
    isDemoAccount: false,
    demoData: null as any, // Adding any as this field will be ignored
    payload: previousWeekOverviewRequest,
    realQueryOptions: {
      staleTime: 5 * 60 * 1000,
    },
  });

  const { percent, percentChange, unhappyCallCount } = useMemo(() => {
    if (currentWeekOverview && previousWeekOverview) {
      const unhappyCallCount = currentWeekOverview.sentiments?.[CallIntelTypes.SentimentEnum.SENTIMENT_NEGATIVE] ?? 0;
      const percent = totalCallsAnalyzed ? Math.round((unhappyCallCount / totalCallsAnalyzed) * 100) : 0;

      const previousWeekUnhappyCallCount =
        previousWeekOverview.sentiments?.[CallIntelTypes.SentimentEnum.SENTIMENT_NEGATIVE] ?? 0;

      const previousWeekPercent = previousWeekTotalCallsAnalyzed
        ? Math.round((previousWeekUnhappyCallCount / previousWeekTotalCallsAnalyzed) * 100)
        : 0;

      const percentChange = percent - previousWeekPercent;

      return {
        unhappyCallCount,
        percent,
        percentChange,
      };
    }
    return {
      percent: 0,
      percentChange: 0,
      unhappyCallCount: 0,
    };
  }, [currentWeekOverview, previousWeekOverview, totalCallsAnalyzed, previousWeekTotalCallsAnalyzed]);

  return (
    <div css={styles.summaryPanel}>
      <Text as='span' weight='bold' size='large' css={styles.countHeader}>
        {unhappyCallCount}
      </Text>
      <div css={styles.content}>
        <UnhappyCallsBadge />
        <div css={{ display: 'flex', flexDirection: 'column', flex: 1, justifyContent: 'flex-end' }}>
          <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
            <Text color='subdued' as='span' weight='bold' css={{ fontSize: theme.font.size.h2 }}>
              {percent}%
            </Text>
            {!!unhappyCallCount && !!totalCallsAnalyzed && <PercentChange percentChange={percentChange} />}
          </div>
          <Text as='span' color='subdued' size='medium'>
            {t('of {{count}} Calls Analyzed', { count: totalCallsAnalyzed || 0 })}
          </Text>
        </div>
      </div>
    </div>
  );
};

const PercentChange = ({ percentChange }: { percentChange: number }) => {
  const { t } = useTranslation('callIntelDashboardWidgets');
  const { triggerProps, tooltipProps, Tooltip } = useTooltip({
    placement: 'top',
  });

  const { iconColor, fontColor, iconName, value, tooltipText } = useMemo(() => {
    const changeType = percentChange > 0 ? 'increase' : percentChange < 0 ? 'decrease' : 'neutral';
    const iconColor: IconColorType =
      changeType === 'decrease' ? 'success' : changeType === 'increase' ? 'error' : 'subdued';
    const fontColor: FontColorType =
      changeType === 'decrease' ? 'success' : changeType === 'increase' ? 'error' : 'subdued';
    const iconName: IconName = changeType === 'decrease' ? 'down-small' : 'up-small';
    const value = Math.abs(percentChange);

    const tooltipText =
      changeType === 'decrease'
        ? t('Down {{count}}% since Last Week.', { count: value })
        : changeType === 'increase'
        ? t('Up {{count}}% since Last Week.', { count: value })
        : t('No change since Last Week.');

    return {
      iconColor,
      fontColor,
      iconName,
      value,
      tooltipText,
    };
  }, [percentChange, t]);

  return (
    <div css={[commonStyles.flexCenter, { gap: theme.spacing(0.25) }]} {...triggerProps}>
      <Icon name={iconName} color={iconColor} size={12} />
      <Text as='span' color={fontColor} size='small'>
        {value}%
      </Text>
      <Tooltip {...tooltipProps}>
        <Text size='medium' as='div' color='white'>
          {tooltipText}
        </Text>
        <Text size='medium' as='div' color='white' css={{ fontStyle: 'italic' }}>
          {t('*Partial comparison. Current time period is ongoing.')}
        </Text>
      </Tooltip>
    </div>
  );
};

const UnhappyCallsBadge = () => {
  const { t } = useTranslation('callIntelDashboardWidgets');

  return (
    <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}>
      <CallIntelChip
        dataKey={CallIntelTypes.SentimentEnum.SENTIMENT_NEGATIVE}
        label={''}
        chipVariants={{
          [CallIntelTypes.SentimentEnum.SENTIMENT_NEGATIVE]: {
            variant: 'critical',
            leftIconName: 'emoji-sad-small',
          },
        }}
      />
      <Text color='subdued' size='medium'>
        {t('Unhappy Calls')}
      </Text>
    </div>
  );
};

const styles = {
  summaryPanel: css`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  `,
  countHeader: css`
    font-size: ${theme.fontSize(138)};
    line-height: 1;
    @container (max-width: ${breakpoints.small.min}px) {
      font-size: ${theme.fontSize(128)};
    }
  `,

  content: css`
    display: flex;
    flex-direction: column;
    flex: 1;
    justify-content: flex-end;
    gap: ${theme.spacing(2)};
    @container (max-width: ${breakpoints.small.min}px) {
      flex-direction: row;
      justify-content: flex-start;
      align-items: flex-end;
    }
  `,
};
